/**
 * Mail server addresses for IMAP/SMTP mailboxes: the security modes and ports DeskWorks One
 * allows, and validation of server names and calendar addresses a person types in.
 *
 * Plaintext is never offered: every connection is either TLS from the first byte ("SSL/TLS")
 * or upgraded with STARTTLS before anything else is sent, and certificates are always verified.
 * Pure functions only (no URL/DNS APIs), so web, desktop, mobile and server agree exactly.
 */

export type MailSecurity = 'implicit-tls' | 'starttls';
export type MailProtocol = 'imap' | 'smtp';

/** The only ports allowed, each with the one security mode it uses. */
export const MAIL_PORTS: Readonly<Record<MailProtocol, Readonly<Record<number, MailSecurity>>>> = Object.freeze({
  imap: Object.freeze({ 993: 'implicit-tls', 143: 'starttls' } as Record<number, MailSecurity>),
  smtp: Object.freeze({ 465: 'implicit-tls', 587: 'starttls' } as Record<number, MailSecurity>),
});

/** Port for a protocol + security mode (993/143 for IMAP, 465/587 for SMTP). */
export function mailPortFor(protocol: MailProtocol, security: MailSecurity): number {
  const entry = Object.entries(MAIL_PORTS[protocol]).find(([, s]) => s === security)!;
  return Number(entry[0]);
}

export function isAllowedMailPort(protocol: MailProtocol, port: number, security: MailSecurity): boolean {
  return MAIL_PORTS[protocol][port] === security;
}

/** Allowed ports for calendar/contacts (https only). */
export const DAV_PORTS: readonly number[] = Object.freeze([443, 8443]);

/** Names that only make sense inside a private network. */
const INTERNAL_SUFFIXES = ['localhost', 'local', 'internal', 'intranet', 'lan', 'home', 'corp', 'localdomain', 'home.arpa', 'invalid', 'test', 'example', 'onion'];

const LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Why a server name is not acceptable, or null when it is. Accepts ordinary DNS names only:
 * no IP literals, no single-label names, no names reserved for private networks. Internationalized
 * names must be entered in their xn-- form.
 */
export function mailHostProblem(input: string): string | null {
  const host = input.trim().toLowerCase().replace(/\.$/, '');
  if (!host) return 'Enter the server name.';
  if (host.includes('://') || host.includes('/')) return 'Enter only the server name, for example imap.example.com.';
  if (host.startsWith('[') || host.includes(':')) return 'Enter a server name, not an IP address or port.';
  if (host.length > 253) return 'This server name is too long.';
  if (/[^\x21-\x7e]/.test(host)) return 'Enter the server name in plain letters (xn-- form for international names).';
  const labels = host.split('.');
  if (labels.length < 2) return 'Enter the full server name, for example imap.example.com.';
  if (!labels.every((l) => LABEL.test(l))) return 'This is not a valid server name.';
  if (/^\d+$/.test(labels[labels.length - 1]!)) return 'Enter a server name, not an IP address.';
  if (INTERNAL_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`))) return 'Servers on private networks can’t be used.';
  return null;
}

export function normalizeMailHost(input: string): string {
  return input.trim().toLowerCase().replace(/\.$/, '');
}

/**
 * Parses a calendar (CalDAV) or contacts (CardDAV) address: https only, a valid public server
 * name, port 443 or 8443, no user name or password inside the address, no query or fragment.
 */
export function parseDavUrl(input: string): { ok: true; url: string; host: string; port: number } | { ok: false; problem: string } {
  const raw = input.trim();
  const m = raw.match(/^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)(\/[^?#]*)?([?#].*)?$/i);
  if (!m) return { ok: false, problem: 'Enter the full calendar address, starting with https://.' };
  if (m[1]!.toLowerCase() !== 'https') return { ok: false, problem: 'The calendar address must start with https://.' };
  const authority = m[2]!;
  if (authority.includes('@')) return { ok: false, problem: 'Leave the user name and password out of the address.' };
  if (m[4]) return { ok: false, problem: 'Leave the part after ? or # out of the address.' };
  const hp = authority.match(/^([^:]+)(?::(\d{1,5}))?$/);
  if (!hp) return { ok: false, problem: 'Enter a server name, not an IP address.' };
  const host = normalizeMailHost(hp[1]!);
  const hostProblem = mailHostProblem(host);
  if (hostProblem) return { ok: false, problem: hostProblem };
  const port = hp[2] ? Number(hp[2]) : 443;
  if (!DAV_PORTS.includes(port)) return { ok: false, problem: 'Calendar addresses can use port 443 or 8443 only.' };
  const path = m[3] || '/';
  if (/(^|\/)\.\.?(\/|$)/.test(path) || /%2f|%5c|\\/i.test(path)) return { ok: false, problem: 'This is not a valid calendar address.' };
  return { ok: true, url: `https://${host}${port === 443 ? '' : `:${port}`}${path}`, host, port };
}
